import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    technology: '',
    location: '',
    description: '',
    file: null
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Send data to backend
    fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        alert(t('contact.successMsg'));
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          inquiryType: '',
          technology: '',
          location: '',
          description: '',
          file: null
        });
      }
    })
    .catch(error => {
      console.error('Error:', error);
      alert(t('contact.errorMsg'));
    });
  };

  const contactInfo = [
    {
      title: t('contact.generalContact'),
      phone: '+421 904 644 464',
      email: 'info@fbsgroup.sk',
      address: 'Tatranská Lomnica 14649, 059 60 Vysoké Tatry'
    }
  ];

  return (
    <section id="contact" className="section-padding steel-bg" aria-labelledby="contact-heading">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 id="contact-heading" className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto">
            {t('contact.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact information */}
          <div className="lg:col-span-1 space-y-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="tech-card">
                <h3 className="text-lg font-semibold text-white mb-4">{info.title}</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-tech-dark rounded-full flex items-center justify-center">
                      <span className="text-accent-green text-sm">📞</span>
                    </div>
                    <span className="text-gray-300">{info.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-tech-dark rounded-full flex items-center justify-center">
                      <span className="text-accent-green text-sm">✉️</span>
                    </div>
                    <span className="text-gray-300">{info.email}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-tech-dark rounded-full flex items-center justify-center">
                      <span className="text-accent-green text-sm">📍</span>
                    </div>
                    <span className="text-gray-300">{info.address}</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Emergency contact */}
            <div className="tech-card border-red-600/30">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <span className="text-red-500 mr-2">🚨</span>
                {t('contact.emergency')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center">
                    <span className="text-red-500 text-sm">📞</span>
                  </div>
                  <span className="text-red-500 font-semibold">+421 900 123 999</span>
                </div>
                <p className="text-gray-400 text-sm">
                  {t('contact.emergencyDesc')}
                </p>
              </div>
            </div>

            {/* Business hours */}
            <div className="tech-card">
              <h3 className="text-lg font-semibold text-white mb-4">{t('contact.businessHours')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('contact.monFri')}</span>
                  <span className="text-white">7:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('contact.saturday')}</span>
                  <span className="text-white">8:00 - 12:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('contact.sunHolidays')}</span>
                  <span className="text-accent-green">{t('contact.nonstopService')}</span>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="tech-card">
              <h3 className="text-lg font-semibold text-white mb-4">{t('contact.ourLocation')}</h3>
              <div className="aspect-[4/3] rounded-lg overflow-hidden border border-[#2a2a2a]">
                <iframe
                  title="FBS group Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2662.5!2d17.1077!3d48.1486!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476c8943!2sBratislava!5e0!3m2!1ssk!2ssk!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2">
            <div className="tech-card">
              <h3 className="text-2xl font-bold text-white mb-6">{t('contact.techConsultation')}</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6" aria-label={t('contact.techConsultation')}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">{t('contact.nameLabel')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                      placeholder={t('contact.namePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">{t('contact.companyLabel')}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                      placeholder={t('contact.companyPlaceholder')}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">{t('contact.emailLabel')}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                      placeholder={t('contact.emailPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">{t('contact.phoneLabel')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                      placeholder="+421 904 644 464"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">{t('contact.inquiryTypeLabel')}</label>
                    <select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                    >
                      <option value="">{t('contact.selectType')}</option>
                      <option value="consultation">{t('contact.typeConsultation')}</option>
                      <option value="project">{t('contact.typeProject')}</option>
                      <option value="service">{t('contact.typeService')}</option>
                      <option value="offer">{t('contact.typeOffer')}</option>
                      <option value="other">{t('contact.typeOther')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">{t('contact.technologyLabel')}</label>
                    <select
                      name="technology"
                      value={formData.technology}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                    >
                      <option value="">{t('contact.selectTechnology')}</option>
                      <option value="diesel">{t('contact.techDiesel')}</option>
                      <option value="battery">{t('contact.techBattery')}</option>
                      <option value="cogeneration">{t('contact.techCogeneration')}</option>
                      <option value="transformers">{t('contact.techTransformers')}</option>
                      <option value="switchgears">{t('contact.techSwitchgears')}</option>
                      <option value="multiple">{t('contact.techMultiple')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">{t('contact.locationLabel')}</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none"
                    placeholder={t('contact.locationPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">{t('contact.descriptionLabel')}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-tech-dark border border-gray-600 rounded-lg text-white focus:border-accent-green focus:outline-none resize-none"
                    placeholder={t('contact.descriptionPlaceholder')}
                  ></textarea>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">{t('contact.attachmentLabel')}</label>
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-accent-green transition-colors duration-200 cursor-pointer">
                    <div className="text-gray-400 mb-2">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-400">{t('contact.attachmentDrag')}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('contact.attachmentFormats')}</p>
                    <p className="text-xs text-accent-green mt-2">{t('contact.attachmentHint')}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input type="checkbox" id="privacy" required className="w-4 h-4 text-accent-green bg-tech-dark border-gray-600 rounded focus:ring-accent-green" />
                  <label htmlFor="privacy" className="text-gray-300 text-sm">
                    {t('contact.privacyConsent')}
                  </label>
                </div>

                <button type="submit" className="btn-primary text-sm px-8 py-2.5 mx-auto block">
                  {t('contact.submitBtn')}
                </button>
              </form>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Contact;
